import {
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  InputBase,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import SchoolIcon from "@mui/icons-material/School";
import DinnerDiningIcon from "@mui/icons-material/DinnerDining";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import { RoomData, RoomLayout } from "../../../../types/venue";
import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import {
  BusinessCenter,
  LocalBar,
  SvgIconComponent,
  TheaterComedy,
} from "@mui/icons-material";

// ─── Default layouts ───────────────────────────────────────────────────────────
const DEFAULT_LAYOUTS: RoomLayout[] = [
  { id: "theater", name: "Theater", capacity: 0 },
  { id: "banquet", name: "Banquet", capacity: 0 },
  { id: "cocktail", name: "Cocktail", capacity: 0 },
  { id: "classroom", name: "Classroom", capacity: 0 },
  { id: "boardroom", name: "Boardroom", capacity: 0 },
  { id: "u_shape", name: "U-Shape", capacity: 0 },
  { id: "cabaret", name: "Cabaret", capacity: 0 },
  { id: "other", name: "Other", capacity: 0 },
];

const LAYOUT_ICONS: Record<string, SvgIconComponent> = {
  theater: EventSeatIcon,
  banquet: DinnerDiningIcon,
  cocktail: LocalBar, // add: import LocalBarIcon from "@mui/icons-material/LocalBar"
  classroom: SchoolIcon,
  boardroom: BusinessCenter, // add: import BusinessCenterIcon from "@mui/icons-material/BusinessCenter"
  u_shape: TableRestaurantIcon,
  cabaret: TheaterComedy, // add: import TheaterComedyIcon from "@mui/icons-material/TheaterComedy"
  other: CorporateFareIcon,
};

// ─── Props ─────────────────────────────────────────────────────────────────────
interface RoomsProps {
  rooms: RoomData[];
  onRoomsChange: (rooms: RoomData[]) => void;
}

// ─── Styled components ─────────────────────────────────────────────────────────
const FieldLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "textcolor",
})<{ textcolor?: string }>(({ textcolor }) => ({
  fontSize: "13px",
  fontWeight: 500,
  opacity: 0.9,
  color: textcolor,
  marginBottom: "6px",
  lineHeight: 1.4,
}));

const SectionLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "textcolor",
})<{ textcolor?: string }>(({ textcolor }) => ({
  fontSize: "14px",
  fontWeight: 600,
  color: textcolor,
  marginBottom: "12px",
  marginTop: "24px",
  paddingBottom: "6px",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
}));

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== "inputbg" && prop !== "textcolor" && prop !== "placeholdercolor",
})<{ inputbg?: string; textcolor?: string; placeholdercolor?: string }>(
  ({ inputbg, textcolor, placeholdercolor }) => ({
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: inputbg,
      borderRadius: "30px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "13px",
      color: textcolor,
      "&::placeholder": {
        color: placeholdercolor,
        opacity: 0.9,
      },
    },
  }),
);

// ─── Layout Card ───────────────────────────────────────────────────────────────
function LayoutCard({
  layout,
  onCapacityChange,
}: {
  layout: RoomLayout;
  onCapacityChange: (value: string) => void;
}) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color900 = reduxTheme.primary_colors?.[900];
  const Icon = LAYOUT_ICONS[layout.id] ?? CorporateFareIcon;

  return (
    <Box
      sx={{
        backgroundColor: color800,
        borderRadius: "12px",
        padding: "16px",
        minWidth: "150px",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "12px" }}
      >
        <Box
          sx={{
            width: "28px",
            height: "28px",
            backgroundColor: color900,
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: "16px", color: color700 }} />
        </Box>
        <Typography sx={{ fontSize: "12px", fontWeight: 400, color: color700 }}>
          {layout.name}
        </Typography>
      </Box>
      <InputBase
        value={layout.capacity || ""}
        placeholder="0"
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "" || /^\d+$/.test(raw)) onCapacityChange(raw);
        }}
        sx={{
          width: "100%",
          backgroundColor: color900,
          borderRadius: "48px",
          padding: "8px 4px",
          fontSize: "10px",
          fontWeight: 400,
          color: color700,
          "& input": { padding: 0, textAlign: "center" },
        }}
      />
    </Box>
  );
}

// ─── Upload Box ────────────────────────────────────────────────────────────────
function UploadBox({
  label,
  onFilesSelected,
  borderColor,
  textcolor,
}: {
  label: string;
  onFilesSelected: (files: File[]) => void;
  borderColor?: string;
  textcolor?: string;
}) {
  const handleClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = label.toLowerCase().includes("floor")
      ? "image/*,application/pdf"
      : "image/*";
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files ?? []);
      onFilesSelected(files);
    };
    input.click();
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        flex: 1,
        minWidth: 200,
        border: `2px dashed ${borderColor}`,
        borderRadius: "16px",
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "opacity 0.2s",
        "&:hover": { opacity: 0.75 },
      }}
    >
      <CloudUploadOutlinedIcon
        sx={{ color: borderColor, fontSize: 32, mb: 1 }}
      />
      <Typography variant="body2" sx={{ color: textcolor, fontSize: "13px" }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── Yes/No Toggle ─────────────────────────────────────────────────────────────
function YesNoToggle({
  value,
  onChange,
  color100,
  color700,
  color800,
  color900,
}: {
  value: "yes" | "no";
  onChange: (val: "yes" | "no") => void;
  color100?: string;
  color700?: string;
  color800?: string;
  color900?: string;
}) {
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      {(["yes", "no"] as const).map((opt) => (
        <Box
          key={opt}
          onClick={() => onChange(opt)}
          sx={{
            flex: 1,
            textAlign: "center",
            py: "10px",
            borderRadius: "30px",
            fontSize: "13px",
            cursor: "pointer",
            fontWeight: value === opt ? 600 : 400,
            backgroundColor: value === opt ? color100 : color800,
            color: value === opt ? color900 : color700,
            transition: "all 0.2s",
          }}
        >
          {opt.charAt(0).toUpperCase() + opt.slice(1)}
        </Box>
      ))}
    </Box>
  );
}

// ─── Room Form ─────────────────────────────────────────────────────────────────
function RoomForm({
  room,
  onUpdate,
}: {
  room: RoomData;
  onUpdate: (room: RoomData) => void;
}) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color100 = reduxTheme.primary_colors?.[100];
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color300 = reduxTheme.primary_colors?.[300];
  const color900 = reduxTheme.primary_colors?.[900];

  const update = (field: keyof RoomData, value: unknown) =>
    onUpdate({ ...room, [field]: value });

  const changeLayoutCapacity = (layoutId: string, val: string) =>
    update(
      "layouts",
      room.layouts.map((l) =>
        l.id === layoutId
          ? { ...l, capacity: val === "" ? 0 : Number(val) }
          : l,
      ),
    );

  const numericOnly = (val: string) => val === "" || /^\d+$/.test(val);

  return (
    <Box sx={{ p: 3 }}>
      {/* ── ROOM BASICS ── */}
      <SectionLabel textcolor={color100}>Room Details</SectionLabel>
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <FieldLabel textcolor={color100}>
            Room Name <span style={{ color: "red" }}>*</span>
          </FieldLabel>
          <StyledTextField
            placeholder="e.g. Chapel Hall"
            value={room.name}
            onChange={(e) => update("name", e.target.value)}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <FieldLabel textcolor={color700}>Capacity</FieldLabel>
          <StyledTextField
            placeholder="e.g. 250"
            value={room.capacity}
            onChange={(e) => {
              if (numericOnly(e.target.value))
                update("capacity", e.target.value);
            }}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <FieldLabel textcolor={color700}>Room Type</FieldLabel>
          <StyledTextField
            placeholder="e.g. Chapel"
            value={room.room_type}
            onChange={(e) => update("room_type", e.target.value)}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
      </Box>

      {/* ── LAYOUTS ── */}
      <SectionLabel textcolor={color100}>Setup &amp; Layouts</SectionLabel>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: "nowrap",
          overflowX: "auto",
          overflowY: "visible",
          mx: -3,
          px: 3,
          pb: 1,
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {room.layouts.map((layout) => (
          <LayoutCard
            key={layout.id}
            layout={layout}
            onCapacityChange={(val) => changeLayoutCapacity(layout.id, val)}
          />
        ))}
      </Box>

      {/* ── INTERNET ── */}
      <SectionLabel textcolor={color100}>Internet Details</SectionLabel>
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <FieldLabel textcolor={color700}>
            Internet Service Provider
          </FieldLabel>
          <StyledTextField
            placeholder="e.g. TelOne"
            value={room.internet_service_provider}
            onChange={(e) =>
              update("internet_service_provider", e.target.value)
            }
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <FieldLabel textcolor={color700}>Upload Speed (Mbps)</FieldLabel>
          <StyledTextField
            placeholder="e.g. 50"
            value={room.internet_upload_speed}
            onChange={(e) => {
              if (numericOnly(e.target.value))
                update("internet_upload_speed", e.target.value);
            }}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <FieldLabel textcolor={color700}>
            Router Distance from Livestream (m)
          </FieldLabel>
          <StyledTextField
            placeholder="e.g. 10"
            value={room.router_distance_from_livestream}
            onChange={(e) => {
              if (numericOnly(e.target.value))
                update("router_distance_from_livestream", e.target.value);
            }}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <FieldLabel textcolor={color700}>WiFi Name</FieldLabel>
          <StyledTextField
            placeholder="e.g. VenueWifi"
            value={room.wifi_name}
            onChange={(e) => update("wifi_name", e.target.value)}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <FieldLabel textcolor={color700}>WiFi Password</FieldLabel>
          <StyledTextField
            placeholder="Password"
            value={room.wifi_password}
            onChange={(e) => update("wifi_password", e.target.value)}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <FieldLabel textcolor={color700}>Router Accessible?</FieldLabel>
          <YesNoToggle
            value={room.router_accessibility}
            onChange={(val) => update("router_accessibility", val)}
            color100={color100}
            color700={color700}
            color800={color800}
            color900={color900}
          />
        </Box>
      </Box>

      {/* ── PHOTOS & FLOORPLANS ── */}
      <SectionLabel textcolor={color100}>Photos &amp; Floorplans</SectionLabel>
      <Box
        sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", md: "nowrap" } }}
      >
        <UploadBox
          label={`Upload Room Photos${room.photos.length > 0 ? ` (${room.photos.length} selected)` : ""}`}
          onFilesSelected={(files) =>
            update("photos", [...room.photos, ...files])
          }
          borderColor={color300}
          textcolor={color700}
        />
        <UploadBox
          label={`Upload Floorplans${room.floorplans.length > 0 ? ` (${room.floorplans.length} selected)` : ""}`}
          onFilesSelected={(files) =>
            update("floorplans", [...room.floorplans, ...files])
          }
          borderColor={color300}
          textcolor={color700}
        />
      </Box>
    </Box>
  );
}

// ─── Fresh draft ───────────────────────────────────────────────────────────────
const freshDraft = (): RoomData => ({
  id: `draft-${Date.now()}`,
  name: "",
  capacity: "",
  room_type: "",
  layouts: DEFAULT_LAYOUTS.map((l) => ({ ...l })),
  photos: [],
  floorplans: [],
  // Internet
  internet_service_provider: "",
  wifi_name: "",
  wifi_password: "",
  internet_upload_speed: "",
  router_accessibility: "yes",
  router_distance_from_livestream: "",
});

// ─── Main Export ───────────────────────────────────────────────────────────────
export default function Rooms({ rooms, onRoomsChange }: RoomsProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color100 = reduxTheme.primary_colors?.[100];
  const color700 = reduxTheme.primary_colors?.[700];
  const color300 = reduxTheme.primary_colors?.[300];

  const [expandedRoom, setExpandedRoom] = useState<string | false>(false);
  const [draftRoom, setDraftRoom] = useState<RoomData>(freshDraft);
  const [draftError, setDraftError] = useState("");

  const addRoom = () => {
    if (!draftRoom.name.trim()) {
      setDraftError("Room name is required before adding.");
      return;
    }
    setDraftError("");
    onRoomsChange([...rooms, { ...draftRoom, id: `room-${Date.now()}` }]);
    setDraftRoom(freshDraft());
  };

  const updateRoom = (updated: RoomData) =>
    onRoomsChange(rooms.map((r) => (r.id === updated.id ? updated : r)));

  const deleteRoom = (id: string) => {
    onRoomsChange(rooms.filter((r) => r.id !== id));
    if (expandedRoom === id) setExpandedRoom(false);
  };

  const displayName = (room: RoomData, i: number) =>
    room.name.trim() || `Room ${i + 1}`;

  return (
    <Box sx={{ width: "100%", minHeight: 400 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addRoom}
          sx={{
            backgroundColor: color100,
            textTransform: "none",
            borderRadius: "8px",
            px: 1,
            "&:hover": { backgroundColor: color700 },
          }}
        >
          Add Room
        </Button>
      </Box>

      {/* Draft form */}
      <Box
        sx={{
          border: `1px solid ${draftError ? "red" : "#e5e7eb"}`,
          borderRadius: "12px",
          mb: 1,
        }}
      >
        <RoomForm
          room={draftRoom}
          onUpdate={(r) => {
            setDraftError("");
            setDraftRoom(r);
          }}
        />
      </Box>
      {draftError && (
        <Typography sx={{ color: "red", fontSize: "12px", mb: 2, pl: 1 }}>
          {draftError}
        </Typography>
      )}

      {/* Saved rooms */}
      {rooms.map((room, i) => (
        <Accordion
          key={room.id}
          expanded={expandedRoom === room.id}
          onChange={(_, isExpanded) =>
            setExpandedRoom(isExpanded ? room.id : false)
          }
          elevation={0}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: "12px !important",
            mb: 2,
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: color700 }} />}
            sx={{
              backgroundColor: "transparent",
              borderBottom:
                expandedRoom === room.id ? "1px solid #e5e7eb" : "none",
              "& .MuiAccordionSummary-content": { my: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                pr: 1,
              }}
            >
              <Typography
                sx={{ fontWeight: 500, fontSize: "13px", color: color100 }}
              >
                {displayName(room, i)}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteRoom(room.id);
                }}
                sx={{ color: "red", opacity: 0.7, "&:hover": { opacity: 1 } }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <RoomForm room={room} onUpdate={updateRoom} />
          </AccordionDetails>
        </Accordion>
      ))}

      {rooms.length === 0 && (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography sx={{ color: color300, fontSize: "13px" }}>
            Added rooms will appear here
          </Typography>
        </Box>
      )}
    </Box>
  );
}
